import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Educator, EducatorStatus } from '@/types/admin'
import { Edit, Trash, ExternalLink, FileText, Eye } from 'lucide-react'

interface EducatorsTableProps {
  educators: Educator[]
  onEdit: (educator: Educator) => void
  onDelete: (id: string) => void
}

const statusMap: Record<EducatorStatus, { label: string; color: string }> = {
  new: { label: 'Novo', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  contacted: {
    label: 'Contactado',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  referred: {
    label: 'Indicado',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  hired: {
    label: 'Contratado',
    color: 'bg-green-100 text-green-800 border-green-200',
  },
}

export function EducatorsTable({
  educators,
  onEdit,
  onDelete,
}: EducatorsTableProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="font-semibold text-slate-700">
              Educador / Contato
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Cargo / Local
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Experiência
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Disponibilidade
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Status
            </TableHead>
            <TableHead className="text-right font-semibold text-slate-700">
              Ações
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {educators.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center py-10 text-slate-500"
              >
                Nenhum educador encontrado com os filtros atuais.
              </TableCell>
            </TableRow>
          ) : (
            educators.map((edu) => (
              <TableRow key={edu.id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium">
                  <div className="text-slate-900">{edu.full_name}</div>
                  <div className="text-xs text-slate-500 flex flex-col mt-0.5">
                    <span>{edu.email}</span>
                    <span className="font-mono">{edu.whatsapp}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-800">
                    {edu.desired_position}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {edu.city}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{edu.experience_years} anos</div>
                  {edu.last_position && (
                    <div
                      className="text-xs text-slate-500 truncate max-w-[150px]"
                      title={edu.last_position}
                    >
                      {edu.last_position}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {edu.immediate_availability ? (
                    <Badge
                      variant="secondary"
                      className="bg-emerald-100 text-emerald-800 border-emerald-200"
                    >
                      Sim, Imediata
                    </Badge>
                  ) : (
                    <span className="text-sm text-slate-500">Aviso Prévio</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`font-normal ${statusMap[edu.status]?.color || ''}`}
                  >
                    {statusMap[edu.status]?.label || edu.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {edu.resume_url ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        title="Ver Currículo"
                      >
                        <a
                          href={edu.resume_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FileText className="h-4 w-4 text-blue-600" />
                        </a>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled
                        title="Sem Currículo"
                      >
                        <FileText className="h-4 w-4 text-slate-300" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(edu)}
                      title="Ver Perfil / Editar"
                    >
                      <Eye className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(edu.id)}
                      title="Excluir"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
